import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useApi } from '@/hooks'

import AlertStatusesContext from './AlertStatusesContext'

import type { AlertStatusesProviderProps, StatusRecord } from '../types'

const AlertStatusesProvider: React.FC<AlertStatusesProviderProps> = ({ alertConfigId, companyId, children }) => {
  const { api } = useApi()
  const [statuses, setStatuses] = useState<StatusRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const refreshStatuses = useCallback(async () => {
    setIsLoading(true)
    setError('')

    try {
      const response = await api.get<StatusRecord[]>({
        path:
          '/alerts/acknowledgement/statuses' +
          '?alertConfigId=' +
          encodeURIComponent(alertConfigId) +
          '&companyId=' +
          encodeURIComponent(companyId),
        origin: 'AlertStatusesProvider#refreshStatuses',
      })

      if (response.error) {
        setStatuses([])
        setError('Unable to load statuses. Try again later.')
        return
      }

      setStatuses(Array.isArray(response.data) ? response.data : [])
    } catch {
      setStatuses([])
      setError('Unable to load statuses. Try again later.')
    } finally {
      setIsLoading(false)
    }
  }, [alertConfigId, companyId, api])

  useEffect(() => {
    void refreshStatuses()
  }, [refreshStatuses])

  const createStatus = useCallback(
    async (statusName: string) => {
      try {
        const response = await api.post({
          path: '/alerts/acknowledgement/statuses/',
          origin: 'AlertStatusesProvider#createStatus',
          body: {
            companyId,
            alertConfigId,
            statusName,
          },
        })

        if (response.error) {
          return 'Unable to create status. Try again later.'
        }

        setStatuses((prev) => {
          const minId = prev.reduce((min, s) => (s.id < min ? s.id : min), 0)
          const tempId = minId <= 0 ? minId - 1 : -1
          return [
            ...prev,
            {
              id: tempId,
              statusName,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            } as StatusRecord,
          ]
        })

        refreshStatuses()
        return ''
      } catch {
        return 'Unable to create status. Try again later.'
      }
    },
    [alertConfigId, companyId, api, refreshStatuses]
  )

  const updateStatus = useCallback(
    async (statusId: number, statusName: string) => {
      try {
        const response = await api.put({
          path: '/alerts/acknowledgement/statuses/' + statusId + '?companyId=' + encodeURIComponent(companyId),
          origin: 'AlertStatusesProvider#updateStatus',
          body: { statusName },
        })

        if (response.error) {
          return 'Unable to update status. Try again later.'
        }

        setStatuses((prev) =>
          prev.map((status) => {
            if (status.id === statusId) {
              return {
                ...status,
                statusName,
                updatedAt: new Date().toISOString(),
              }
            }
            return status
          })
        )

        refreshStatuses()
        return ''
      } catch {
        return 'Unable to update status. Try again later.'
      }
    },
    [companyId, api, refreshStatuses]
  )

  const deleteStatus = useCallback(
    async (statusId: number) => {
      try {
        const response = await api.remove({
          path: '/alerts/acknowledgement/statuses/' + statusId + '?companyId=' + encodeURIComponent(companyId),
          origin: 'AlertStatusesProvider#deleteStatus',
        })

        if (response.error) {
          return 'Unable to delete status. Try again later.'
        }

        setStatuses((prev) => prev.filter((status) => status.id !== statusId))

        refreshStatuses()
        return ''
      } catch {
        return 'Unable to delete status. Try again later.'
      }
    },
    [companyId, api, refreshStatuses]
  )

  const value = useMemo(
    () => ({
      statuses,
      isLoading,
      error,
      refreshStatuses,
      createStatus,
      updateStatus,
      deleteStatus,
    }),
    [statuses, isLoading, error, refreshStatuses, createStatus, updateStatus, deleteStatus]
  )

  return <AlertStatusesContext.Provider value={value}>{children}</AlertStatusesContext.Provider>
}

export default AlertStatusesProvider
